import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { apiRequest } from "shared/api/apiRequest";
import { getSocietyDashboardIdContract } from "shared/api/contracts/society/societyId/dashboards/dashboardId";
import Spinner from "shared/components/common/Spinner";
import { useSocietySession } from "shared/context/SocietySessionContext";
import { useSocietyColorPalette } from "shared/hooks/useSocietyColorPalette";

const StackedBarChartModuleType = ({
  dashboardId,
}: {
  dashboardId: number;
}) => {
  const { societyId } = useSocietySession();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboardModule", dashboardId],
    queryFn: () =>
      apiRequest({
        contract: getSocietyDashboardIdContract,
        inputs: {
          params: { societyId, dashboardId },
        },
      }),
    refetchOnWindowFocus: false,
  });

  const { paletteList } = useSocietyColorPalette(true);

  if (
    (isLoading && !data) ||
    !data ||
    !data.data ||
    !Array.isArray(data.data)
  ) {
    return (
      <div className="col-span-6 flex h-50 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const { dashboardModule, data: results } = data;

  const isHorizontal = dashboardModule.componentConfig?.layout === "horizontal";

  const name = dashboardModule.valueConfig?.find(
    (config: { title?: string }) => config.title !== "name",
  )?.title;

  return (
    <div className="h-50 flex flex-col">
      <span className="mb-2 block w-full text-center text-base font-semibold">
        {dashboardModule.title}
      </span>

      <ResponsiveContainer>
        <BarChart
          data={data.data}
          layout={isHorizontal ? "vertical" : "horizontal"}
        >
          <Tooltip />
          <CartesianGrid strokeDasharray="3 3" />
          {isHorizontal ? (
            <>
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" />
            </>
          ) : (
            <>
              <XAxis dataKey="name" type="category" />
              <YAxis type="number" />
            </>
          )}
          <Bar dataKey={"value"} name={name}>
            {results.map((entry, index) => {
              const fill =
                dashboardModule.setsConfig?.find(
                  (f) => f.dataKey === entry.name,
                )?.fill ?? paletteList[index % paletteList.length];
              return <Cell key={`cell-${index}`} fill={fill} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default StackedBarChartModuleType;
